import {formatDateStringAsKey, getStartOfDay, nonEmpty, numericCompareDesc, stringCompare} from '@lib/utils';
import {createSelector} from '@reduxjs/toolkit';
import {isSameDay, parseISO} from 'date-fns';
import pickBy from 'lodash/pickBy';
import createCachedSelector from 're-reselect';
import {RootState} from '../../rootReducer';
import {getAllConnectionsCheckInStatus} from '../checkIn';
import {Connection, Connections, ConnectionUser} from '../entities';
import {getWorkspaceReservationSpaceNodeId} from '../workspaceReservation/utils';
import groupBy from 'lodash/groupBy';

import {
  connectedUsersAdapter,
  connectionsAdapter,
  connectionSearchResultsAdapter,
  connectionsWorkdaysAdapter,
  connectionsWorkspaceReservationsAdapter,
} from './adapters';
import {ConnectionUserDto, WorkdayDto, WorkspaceReservationDto} from '@lib/infrastructure';
import {cachedComplexProps, simpleCachedComplexPropsHasher} from '../../util';

export const {
  selectById: getConnectionById,
  selectIds: getConnectionIds,
  selectEntities: getConnectionEntities,
  selectAll: getAllConnections,
  selectTotal: getTotalConnections,
} = connectionsAdapter.getSelectors((state: RootState) => state.data.connections.connections);

export const {
  selectById: getConnectedUserById,
  selectIds: getConnectedUserIds,
  selectEntities: getConnectedUserEntities,
  selectAll: getAllConnectedUsers,
  selectTotal: getTotalConnectedUsers,
} = connectedUsersAdapter.getSelectors((state: RootState) => state.data.connections.connectedUsers);

export const {
  selectById: getConnectionSearchResultById,
  selectIds: getConnectionSearchResultIds,
  selectEntities: getConnectionSearchResultEntities,
  selectAll: getAllConnectionSearchResults,
  selectTotal: getTotalConnectionSearchResults,
} = connectionSearchResultsAdapter.getSelectors((state: RootState) => state.data.connections.searchResults);

export const {
  selectById: getWorkspaceReservationsForConnectionsById,
  selectIds: getWorkspaceReservationsForConnectionsIds,
  selectEntities: getWorkspaceReservationsForConnectionsEntities,
  selectAll: getAllWorkspaceReservationsForConnections,
  selectTotal: getTotalWorkspaceReservationsForConnections,
} = connectionsWorkspaceReservationsAdapter.getSelectors(
  (state: RootState) => state.data.connections.workspaceReservations,
);

export const {
  selectById: getWorkdayForConnectionsById,
  selectIds: getWorkdaysForConnectionsIds,
  selectEntities: getWorkdaysForConnectionsEntities,
  selectAll: getAllWorkdaysForConnections,
  selectTotal: getTotalWorkdaysForConnections,
} = connectionsWorkdaysAdapter.getSelectors((state: RootState) => state.data.connections.workdays);

export const getIsLoadingConnections = (state: RootState) => state.data.connections.isLoadingConnections;

export const getIsLoadingWorkspaceReservationsForConnections = (state: RootState) =>
  state.data.connections.isLoadingWorkspaceReservationsForConnections;

export const getPendingIncomingConnections = createSelector(getConnectionEntities, (connections) => {
  const result = pickBy<Connection>(
    connections as Connections,
    (conn) => conn.status === 'Pending' && conn.direction === 'Incoming',
  );
  return Object.values(result);
});

export const getPendingIncomingConnectionCount = createSelector(getPendingIncomingConnections, (conn) => conn.length);

export const getPendingOutgoingConnections = createSelector(getConnectionEntities, (connections) => {
  const result = pickBy<Connection>(
    connections as Connections,
    (conn) => conn.status === 'Pending' && conn.direction === 'Outgoing',
  );
  return Object.values(result);
});

export const getAcceptedConnections = createSelector(getConnectionEntities, (connections) => {
  const result = pickBy<Connection>(connections as Connections, (conn) => conn.status === 'Accepted');
  return Object.values(result);
});

export const getAcceptedConnectedUsers = createSelector(
  getConnectedUserEntities,
  getAcceptedConnections,
  (connectedUserEntities, acceptedConnections) => {
    return acceptedConnections.map(({connectedUser}) => connectedUserEntities[connectedUser]) as ConnectionUser[];
  },
);

export const getIgnoredConnections = createSelector(getConnectionEntities, (connections) => {
  const result = pickBy<Connection>(connections as Connections, (conn) => conn.status === 'Ignored');
  return Object.values(result);
});

export const getIsSearching = (state: RootState) => state.data.connections.isSearching;

export const getConnectedUsersGroupedByWorkspaceReservationDate = createSelector(
  getAllWorkspaceReservationsForConnections,
  getConnectedUserEntities,
  (workspaceReservations, connectedUserEntities) => {
    return (
      workspaceReservations
        .filter((workspaceReservation) => workspaceReservation.status === 'Confirmed')
        .reduce((accumulator, current) => {
          const foundConnection = connectedUserEntities[current.userId];
          if (!foundConnection) return accumulator;
          const key = formatDateStringAsKey(current.startDate);
          accumulator[key] = accumulator[key] ? [...accumulator[key], foundConnection] : [foundConnection];
          return accumulator;
        }, {} as Record<string, ConnectionUser[]>) ?? {}
    );
  },
);

export const getWorkdaysForConnectionsByDate = createCachedSelector(
  getAllWorkdaysForConnections,
  (_state: RootState, date: string) => date,
  (workdays, dateString) => {
    const referenceDate = parseISO(dateString);
    return workdays.filter((wd) => isSameDay(parseISO(wd.startDate), referenceDate));
  },
)((_state, date) => date);

export const getSortedConnectedUsersWithOfficeDayOnDate = createCachedSelector(
  getWorkdaysForConnectionsByDate,
  getAllConnectedUsers,
  getAllConnectionsCheckInStatus,
  (workdays, connectedUsers, checkInStatus): ConnectionUser[] => {
    // Connected users, check-in statuses and workdays usually get loaded once.
    // That means it's not worth it to put these indexes in their own cached selectors,
    // since most of the time this selector will read from cache
    const userIdsWithOfficeDay = new Set<string>();
    for (const workday of workdays) {
      if (workday.status === 'OfficeDay') {
        userIdsWithOfficeDay.add(workday.userId);
      }
    }

    const userIdsCheckedIn = new Set<string>();
    for (const checkIn of checkInStatus) {
      if (checkIn.eventType === 'AtTheOffice' && checkIn.userId) userIdsCheckedIn.add(checkIn.userId);
    }

    return connectedUsers
      .filter((user) => userIdsWithOfficeDay.has(user.id))
      .sort(
        (a, b) =>
          // Two groups: checked-in to the front, others to the back
          numericCompareDesc(userIdsCheckedIn.has(a.id) ? 1 : 0, userIdsCheckedIn.has(b.id) ? 1 : 0) ||
          // Within groups, sort alphabetically
          stringCompare(a.name, b.name),
      );
  },
)((_state, date) => date);

export const getWorkspaceReservationsForConnectionsByDate = createCachedSelector(
  getAllWorkspaceReservationsForConnections,
  (_state: RootState, props: {date: string; buildingId?: string; userId?: string; nodeId?: string}) =>
    cachedComplexProps(props, simpleCachedComplexPropsHasher),
  (workspaceReservations, {date, userId, buildingId, nodeId}) => {
    const results = workspaceReservations
      .slice()
      .filter(
        (workspaceReservation) =>
          workspaceReservation.status === 'Confirmed' &&
          isSameDay(getStartOfDay(workspaceReservation.startDate), getStartOfDay(date)) &&
          (!buildingId || (!!buildingId && workspaceReservation.buildingId === buildingId)) &&
          (!userId || (!!userId && workspaceReservation.userId === userId)) &&
          (!nodeId || (!!nodeId && getWorkspaceReservationSpaceNodeId(workspaceReservation) === nodeId)),
      );

    return nonEmpty(results);
  },
)((_state, props) => simpleCachedComplexPropsHasher(props));

export const getConnectedUsersByDate = createCachedSelector(
  getWorkspaceReservationsForConnectionsByDate,
  getConnectedUserEntities,
  (workspaceReservations, connectedUserEntities) => {
    const connectedUsers = workspaceReservations
      .slice()
      .map((workspaceReservation) => connectedUserEntities[workspaceReservation.userId])
      .sort((a, b) => {
        if (a && b) {
          if (a?.name < b?.name) return -1;
          else if (a?.name > b?.name) return 1;
        }
        return 0;
      });
    return nonEmpty(connectedUsers);
  },
)((_state, {date, userId, buildingId, nodeId}) => `${date}:${userId}:${buildingId}:${nodeId}`);

export const getConnectionSuggestion = (state: RootState) => state.data.connections.connectionSuggestion;

export const getIsLoadingConnectionSuggestions = (state: RootState) =>
  state.data.connections.isLoadingConnectionSuggestions;

export const getIsSendingConnectionSuggestionsFeedback = (state: RootState) =>
  state.data.connections.isSendingConnectionSuggestionsFeedback;

export const getIsConnecting = (state: RootState) => state.data.connections.isConnecting;

export const getConnectionWhereaboutsForDay = createCachedSelector(
  [
    (state: RootState) => state.data.connections.connectionsWithReservations,
    (_state: RootState, dateKey: string) => dateKey,
  ],

  (connectionsWithReservations, dateKey) => {
    if (connectionsWithReservations === null) return null;

    const inSomeOffice: {user: ConnectionUserDto; workday: WorkdayDto; workspace: WorkspaceReservationDto | null}[] =
      [];
    const remote: {user: ConnectionUserDto; workday: WorkdayDto; workspace: null}[] = [];
    const notWorking: {user: ConnectionUserDto; workday: WorkdayDto; workspace: null}[] = [];
    const unknown: {user: ConnectionUserDto; workday: null; workspace: null}[] = [];

    for (const {user, workdays, workspaceReservations} of connectionsWithReservations) {
      const workday = workdays.find((wd) => wd.startDate.startsWith(dateKey));

      if (!workday) {
        unknown.push({user, workday: null, workspace: null});
      } else if (workday.status === 'NotWorking') {
        notWorking.push({user, workday, workspace: null});
      } else if (workday.status === 'WorkingRemotely') {
        remote.push({user, workday, workspace: null});
      } else if (workday.status === 'OfficeDay') {
        const workspace = workspaceReservations.find((w) => w.startDate.startsWith(dateKey)) ?? null;
        inSomeOffice.push({user, workday, workspace});
      }
    }

    return {inSomeOffice, remote, notWorking, unknown};
  },
)((_state, dateKey) => dateKey);

export const getConnectionGroupedByDate = createSelector(
  getAllWorkdaysForConnections,
  getConnectedUserEntities,
  (workdaysForConnections, allConnectedUsers) => {
    const connectionsWorkdays = workdaysForConnections.map((wkForConnection) => {
      return {...wkForConnection, user: allConnectedUsers[wkForConnection.userId]};
    });

    const groupedWokrdays = groupBy(connectionsWorkdays, 'startDate');
    return groupedWokrdays;
  },
);
